/** Owner Dashboard redesign (AB#2206) — display formatting (no business rules). */
import { isolate } from '../../../lib/staffDisplay';

/**
 * Money as the booking detail shows it: Latin digits, grouped, currency code
 * after, isolated so it keeps its order inside Arabic text. Whole amounts drop
 * the decimals ("1,240 AED"); others keep two ("120.50 AED").
 */
export function formatMoney(amount: number, currency: string | null | undefined): string {
    const n = Number(amount) || 0;
    const whole = Math.round(n * 100) % 100 === 0;
    const digits = n.toLocaleString('en-GB', {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
    });
    return isolate(currency ? `${digits} ${currency}` : digits);
}

/** "Thursday, 5 November" / Arabic equivalent, for a local "yyyy-MM-dd". */
export function formatLongDay(ymd: string, language: string): string {
    const d = new Date(`${ymd}T00:00:00Z`);
    return new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
    }).format(d);
}

/** "Thu 5 Nov" for a booking's ISO date (wall clock, UTC-pinned). */
export function formatShortDay(iso: string, language: string): string {
    const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
    }).format(d);
}

/** Two-letter initials for an avatar. */
export function initials(name: string | null | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '·';
    const first = Array.from(parts[0])[0] ?? '';
    const last = parts.length > 1 ? (Array.from(parts[parts.length - 1])[0] ?? '') : '';
    return (first + last).toUpperCase();
}
